/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

function generateAddCardOptions(options) {
	const selectAfterAdd =  options && options.selectAfterAdd !== undefined ? options.selectAfterAdd : true;
	return options && Object.assign(options, { insertBefore: options.insertBefore, selectAfterAdd:selectAfterAdd, model: options.model });
}
/**
 * a set of APIs for exploration operations (eg. add card, delete card)
 * @module ExplorationAPI
 */
export default class ExplorationAPI {
	constructor(exploreModel, startingPointsAPI, copyPasteAPI) {
		/**
		 * To be invoked by the product only.
		 */
		this.destroy = function() {
			exploreModel = null;
			startingPointsAPI = null;
		};

		/**
		 * @param {string} id - id of card
		 * @return {CardAPI} represented by the supplied id
		 */
		this.getCard = function(id) {
			return exploreModel.findModel(id).getAPI();
		};

		/**
		 * @return {CardAPI[]} all cards, in order
		 */
		this.getCards = function() {
			return exploreModel.items.map(
				cardModel => cardModel.getAPI()
			);
		};

		/**
		 * Adds a new card
		 * @param {object} params - parameters of operation, includes widgets and model
		 * @param {array}  params.widgetSpecs - widgets spec of card
		 * @param {object} [params.layoutSpec] - layout model of card, if omitted single/regular card will be created
		 * @param {object} [options] - additional options for add operation
		 * @param {string} [options.insertBefore] - id of card to add this card before - if omitted, new card will be last card
		 * @return {string} model id for new card
		 */
		this.addCard = function(params, options) {
			let cardOptions = options;
			if (params.layoutSpec) {
				if (cardOptions) {
					cardOptions.model = params.layoutSpec;
				} else {
					cardOptions = { model:  params.layoutSpec };
				}
			}
			return exploreModel.addVisCard(
				params.widgetSpecs,
				null,
				generateAddCardOptions(cardOptions)
			);
		};

		/**
		 * Delete an array of card(s) for the given card IDs
		 * @param  {string[]} cards IDs
		 * @return {boolean} True if it successfully deleted passed in cards
		 */
		this.deleteCards = function(ids){
			return exploreModel.deleteCards(ids);
		};

		/**
		 * Duplicates an existing card
		 * @param {string} cardID - card to duplicate
		 * @param {object} [options] - additional operations for duplicate operation
		 * @param {string} [options.insertBefore] - id of card ot add this card before
		 */
		this.duplicateCard = function(cardId, options) {
			return exploreModel.duplicateCard(
				exploreModel.findModel(cardId),
				null,
				generateAddCardOptions(options)
			);
		};

		/**
		* Add a driver analysis card for the selected correlation entry.
		* This function will look for an existing driver analysis card that matches the target and the filters used by the correlation entry
		* If one is found, we switch to it and update the selected driver
		* If none is found, then we create a new driver analysis card using the widget spec supplied by the correlation entry
		* @param {Object} correlationEntry - object representing the correlation entry returned by the badges feature
		*/
		this.addDriverAnalysisCardForCorrelation = function(correlationEntry) {
			exploreModel.addCometCardForCorrelation(correlationEntry);
		};

		/**
		 * Get related widgets of current selected widget
		 * @return {object} promise with recommended widgets
		 */
		this.getRelatedVis = function() {
			const widget = exploreModel.exploreVM.selectedWidget;
			if(!widget) {
				throw new Error('No widget is selected');
			}
			return exploreModel.exploreVM.relatedRecommendationVM.recommender.fetchRecommendations(widget,'related');
		};

		/**
		 * Get recommended viz of current selected widget
		 * @return {object} promise with recommended widgets
		 */
		this.getRecommendedVisualizations = function() {
			const widget = exploreModel.exploreVM.selectedWidget;
			if(!widget) {
				throw new Error('No widget is selected');
			}
			return exploreModel.exploreVM.alternativeRecommendationVM.fetchRecommendations(widget,'alternative').then(()=>{
				return exploreModel.exploreVM.alternativeRecommendationVM.getRecommendations();
			});
		};

		/**
		 * Get compare card recommendations of current selected widget
		 * @return {object} promise with recommended compare cards
		 */
		this.getCompareCardRecommendations = function() {
			const widget = exploreModel.exploreVM.selectedWidget;
			if(!widget) {
				throw new Error('No widget is selected');
			}
			const widgetModel = exploreModel.exploreVM.selectedWidgetModel;
			return exploreModel.exploreVM.getCompareCardRecommendations({ widgetApi: widget, widgetModel });
		};

		/**
		 * Change the vis type either by doing recommendation and manual choose
		 * @param {object} recommendation - recommended viz
		 * @param {boolean} isSetByAuto - automatically recommendation or manually choose
		 * @return {object} a promise which would be resolved when the action is done
		 */
		this.changeVisType = function(recommendation, isSetByAuto) {
			return exploreModel.exploreVM.changeVisType(recommendation, isSetByAuto);
		};

		/**
		 * Change to a fixed vis type for a selected widget
		 * @param {string} visId - the id of a vis
		 * @return {object} a promise
		 */
		this.changeFixedVisualizationTypeForSelectedWidget = function(visId) {
			return exploreModel.exploreVM.changeFixedVisType(visId);
		};

		/**
		 * Copy the currently displayed widget(s) in explore into clipboard
		 * @return {object} a promise which would be resolved when the action is done
		 */
		this.copy = function() {
			return copyPasteAPI.copy();
		};

		/**
		 * Paste the copied widget(s) to explore
		 * @return {object} a promise which would be resolved when the action is done
		 */
		this.paste = function() {
			return copyPasteAPI.paste();
		};

		/**
		 * select cards with range or single selection
		 * @param {string} id - id card id to select
		 * @param {object} [options] - select options
		 * @param {boolean} [options.multiSelect] - true, multi select on top of current selections; otherwise, false
		 * @param {boolean} [options.rangeSelect] - true, range select between current card id and current selection; otherwise, false
		 * @return {string} model id for new card
		 */
		this.selectCards = function(id, { rangeSelect = false, multiSelect = false } = {}) {
			let options;
			if (rangeSelect) {
				options = { shift: true };
			} else if (multiSelect) {
				options = { control: true };
			}
			exploreModel.exploreVM.selectedCards.select(id, options);
		};

		/**
		 * delete all selected cards
		 * @return {boolean} True if it successfully deleted passed in cards
		 */
		this.deleteSelectedCards = function(){
			const selectedCardIds = this.getSelectedCardIds();
			return exploreModel.deleteCards(selectedCardIds);
		};

		/**
		 * get cards being selected
		 * @return {string[]} array of card ids
		 */
		this.getSelectedCardIds = function() {
			return exploreModel.exploreVM.selectedCards.items.map((item) => item );
		};

		/**
		 * @return {boolean} true if starting points view is opened
		 */
		this.isStartingPointsDisplayed = function() {
			return exploreModel.exploreVM.isStartingPointsDisplayed();
		};

		/**
		 * Get current displayed card id
		 * @return {string} current displayed card id
		 */
		this.getDisplayedCard = function() {
			return exploreModel.exploreVM.getDisplayedCard();
		};

		/**
		 * Set display to card
		 * @param {string} id card id
		 */
		this.setDisplayedToCard = function(id) {
			return  exploreModel.exploreVM.setDisplayedToCard(id);
		};
		/**
		 * get starting point api;
		 * @return {object} starting point api
		 */
		this.getStartingPointsAPI = function() {
			return startingPointsAPI;
		};

		/**
		 * Set the current active data source with a given data source Id
		 * @param {string} dataSourceId Data Source Id
		 */
		this.setActiveDataSourceId = function(dataSourceId) {
			if(!dataSourceId) {
				throw new Error('No dataSourceId is specified');
			}
			exploreModel.exploreVM.setActiveDataSourceId(dataSourceId);
		};

		/**
		 * @return {string} the active data source id
		 */
		this.getActiveDatasourceId = function() {
			return exploreModel.exploreVM.activeDataSourceId;
		};

		/**
		 * get the current active data source module
		 * @return {Promise<Module>} Return a promise resolved with the data source Module
		 */
		this.getActiveDataSourceModule = function() {
			const activeDataSourceModule = exploreModel.exploreVM.activeDataSourceModule;
			return Promise.resolve(activeDataSourceModule || null);
		};
	}
}



// WEBPACK FOOTER //
// ./src/js/api/ExplorationAPI.js